// v1.7.0 · CRM · Fase 1: contenedor del módulo (permisos, cabecera, pestañas y ficha de contacto)
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, LogOut, Users2 } from 'lucide-react';
import { api, errMsg } from './ui';
import CrmDashboard from './Dashboard';
import CrmContacts from './Contacts';
import ContactDetail from './ContactDetail';
import CrmOpportunities from './Opportunities';
import CrmTasks from './Tasks';
import CrmSettings from './Settings';
import CrmConversations from './Conversations';   // v1.8.0
import CrmCalendar from './Calendar';   // v1.9.0
import CrmPayments from './Payments';   // v1.10.0
import CrmAutomations from './Automations';   // v1.11.0
import CrmReports from './Reports';   // v1.12.0
import CrmAssistant from './Assistant';   // v1.14.0

const CRM_ROLES = ['admin', 'gerencia', 'contratos', 'ventas'];
const TABS = [['dashboard', 'Panel'], ['assistant', 'Asistente'], ['conversations', 'Conversaciones'], ['calendar', 'Calendario'], ['contacts', 'Contactos'],
  ['opportunities', 'Oportunidades'], ['payments', 'Pagos'], ['automations', 'Automatizaciones'],
  ['tasks', 'Tareas'], ['reports', 'Reportes'], ['settings', 'Configuración']];   // v1.12.0 · Reportes   // v1.11.0 · Automatizaciones   // v1.8.0 · Conversaciones · v1.9.0 · Calendario · v1.10.0 · Pagos
const HOME = { contratos: '/contratos', ventas: null, customer: '/customer', mercado: '/mercado', mercado_consulta: '/mercado', qa: '/qa', client_qa: '/qa' };

export const canUseCrm = (u) => !!u && (CRM_ROLES.includes(u.role) || u.username === 'Gerencia' || (u.extra_modules || []).some((m) => ['crm', 'crm_admin'].includes(m)));   // v1.14.0 · también crm_admin

export default function CrmPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [params, setParams] = useSearchParams();
  const [user, setUser] = useState(null);
  const [meta, setMeta] = useState(null);
  const tab = TABS.some(([k]) => k === params.get('tab')) ? params.get('tab') : 'dashboard';

  useEffect(() => {
    const stored = localStorage.getItem('user');
    if (!stored) { navigate('/login'); return; }
    let u = null;
    try { u = JSON.parse(stored); } catch { navigate('/login'); return; }
    // v1.7.1 · sin permiso vuelve a su propio portal (antes a /login) y el aviso sale una sola vez
    if (!canUseCrm(u)) { toast.error('No tiene acceso al CRM', { id: 'crm-denied' }); navigate(HOME[u.role] || '/finanzas'); return; }
    setUser(u);
  }, [navigate]);

  const reloadMeta = useCallback(async () => {
    try { setMeta(await api.get('/meta')); } catch (e) {
      toast.error(errMsg(e));
      if (e?.response?.status === 401) { localStorage.clear(); navigate('/login'); }
    }
  }, [navigate]);
  useEffect(() => { if (user) reloadMeta(); }, [user, reloadMeta]);

  // v1.8.0 · contador de conversaciones no leídas en la pestaña (se refresca cada minuto)
  const [unread, setUnread] = useState(0);
  const [aiPending, setAiPending] = useState(0);   // v1.14.0 · borradores del asistente por aprobar
  const loadCounts = useCallback(async () => {
    try { setUnread((await api.get('/conversations/counts')).unread); } catch { /* sin contador */ }
    try { setAiPending((await api.get('/ai/counts')).pending); } catch { /* sin contador */ }
  }, []);
  useEffect(() => {
    if (!user) return undefined;
    loadCounts();
    const iv = setInterval(loadCounts, 60000);
    return () => clearInterval(iv);
  }, [user, loadCounts]);

  const go = (k) => { setParams({ tab: k }); if (id) navigate(`/crm?tab=${k}`); };
  const home = user ? (user.role in HOME ? HOME[user.role] : '/finanzas') : null;

  return (
    <div className="min-h-screen bg-gray-50" data-testid="crm-page">
      <header className="border-b bg-white shadow-sm">
        <div className="mx-auto flex max-w-[1400px] items-center justify-between gap-3 px-4 py-3.5">
          <div className="flex min-w-0 items-center gap-3">
            {home && (
              <button className="inline-flex items-center gap-1 rounded-lg px-2 py-1.5 text-sm text-gray-600 hover:bg-gray-100" onClick={() => navigate(home)} aria-label="Volver a módulos">
                <ArrowLeft size={16} /> <span className="hidden sm:inline">Módulos</span>
              </button>
            )}
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[#2F3D8E]"><Users2 size={20} className="text-white" /></div>
            <div className="min-w-0">
              <h1 className="text-xl font-bold text-[#2F3D8E]">CRM</h1>
              <p className="truncate text-sm text-gray-500">Contactos, oportunidades y seguimiento comercial</p>
            </div>
          </div>
          <button className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-1.5 text-sm hover:bg-gray-50" onClick={() => { localStorage.clear(); navigate('/login'); }}>
            <LogOut size={15} /> Salir
          </button>
        </div>
      </header>

      <main className="mx-auto max-w-[1400px] px-4 py-5">
        <nav className="mb-4 flex w-full gap-1 overflow-x-auto rounded-xl border bg-white p-1 sm:w-fit" aria-label="Secciones del CRM">
          {TABS.filter(([k]) => !['settings', 'reports'].includes(k) || meta?.is_admin).map(([k, l]) => (   // v1.12.0
            <button key={k} onClick={() => go(k)}
                    className={`whitespace-nowrap rounded-lg px-4 py-2 text-sm font-medium transition ${!id && tab === k ? 'bg-[#2F3D8E] text-white' : 'text-gray-600 hover:bg-gray-100'}`}>
              {l}
              {k === 'assistant' && aiPending > 0 && (
                <span className="ml-1.5 rounded-full bg-violet-600 px-1.5 py-0.5 text-[10px] font-bold text-white" aria-label={`${aiPending} por aprobar`}>{aiPending > 99 ? '99+' : aiPending}</span>
              )}
              {k === 'conversations' && unread > 0 && (
                <span className="ml-1.5 rounded-full bg-[#e34948] px-1.5 py-0.5 text-[10px] font-bold text-white" aria-label={`${unread} sin leer`}>{unread > 99 ? '99+' : unread}</span>
              )}
            </button>
          ))}
        </nav>

        {!meta ? <div className="py-16 text-center text-sm text-gray-500">Cargando CRM…</div>
          : id ? <ContactDetail key={id} meta={meta} contactId={id} />
          : tab === 'dashboard' ? <CrmDashboard meta={meta} onGo={go} />
          : tab === 'assistant' ? <CrmAssistant meta={meta} onCounts={loadCounts} />
          : tab === 'conversations' ? <CrmConversations meta={meta} onCounts={loadCounts} />
          : tab === 'calendar' ? <CrmCalendar meta={meta} />
          : tab === 'payments' ? <CrmPayments meta={meta} />
          : tab === 'automations' ? <CrmAutomations meta={meta} />
          : tab === 'reports' ? <CrmReports meta={meta} />
          : tab === 'contacts' ? <CrmContacts meta={meta} reloadMeta={reloadMeta} />
          : tab === 'opportunities' ? <CrmOpportunities meta={meta} />
          : tab === 'tasks' ? <CrmTasks meta={meta} />
          : <CrmSettings meta={meta} reloadMeta={reloadMeta} />}
      </main>
    </div>
  );
}
